// Security update: HTML rendered from stored content is sanitized with sanitizeHtml() before it is inserted into the page.
/* FAQ section aligned with the Onze werelden styling: cream canvas, dark-green typography, white accordion cards, compact spacing, and WhatsApp contact link updated to +31 85 02 33 564 (same as the phone number). */

import { sanitizeHtml } from '@/lib/sanitize-html';
import { motion } from 'framer-motion';
import { Plus, Minus, Headset } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/lib/supabase';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  display_order: number;
}

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .eq('active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer.replace(/<[^>]*>/g, '')
      }
    }))
  } : null;

  return (
    <section id="faq" data-fast-scroll="viewed_faq" className="relative isolate overflow-hidden bg-[#fcfaf6] py-20 text-[#1e2b22] sm:py-24 lg:py-28">
      {faqSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        </Helmet>
      )}

      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(100%_60%_at_50%_0%,rgba(137,146,127,0.08),transparent_60%)]"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-[1240px] px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="mb-10 max-w-2xl sm:mb-12"
        >
          <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">
            Klantenservice
          </p>
          <h2 className="text-4xl tracking-[-0.035em] text-[#1e2b22] md:text-5xl">
            Veelgestelde vragen
          </h2>
          <p className="mt-3 max-w-xl text-base leading-7 text-[#5a6552] sm:text-lg sm:leading-8">
            Hier vind je antwoorden op de meest gestelde vragen over onze haagplanten, het verzorgen ervan en andere diensten.
          </p>
        </motion.div>

        {loading ? (
          <div className="py-12 text-center text-[#5a6552]">Laden...</div>
        ) : faqs.length === 0 ? (
          <div className="py-12 text-center text-[#5a6552]">Geen vragen beschikbaar op dit moment.</div>
        ) : (
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true }}
                className={`overflow-hidden rounded-xl bg-white ring-1 transition-[box-shadow,ring-color] duration-300 ${
                  openIndex === index ? 'ring-[#1e2b22]/20 shadow-[0_12px_40px_rgba(30,43,34,0.08)]' : 'ring-[#e7e1d6] hover:ring-[#1e2b22]/20'
                }`}
              >
                <button
                  onClick={() => toggleQuestion(index)}
                  className="group flex w-full items-center justify-between px-6 py-5 text-left transition-colors duration-200 hover:bg-[#e7e1d6] sm:px-8 sm:py-6"
                >
                  <span className="text-lg font-semibold text-[#1e2b22] pr-4 sm:text-xl">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-stone-200 text-[#1e2b22] transition-colors duration-200 group-hover:bg-stone-300"
                  >
                    {openIndex === index ? (
                      <Minus className="w-5 h-5" strokeWidth={2.5} />
                    ) : (
                      <Plus className="w-5 h-5" strokeWidth={2.5} />
                    )}
                  </motion.div>
                </button>

                <motion.div
                  initial={false}
                  animate={{
                    height: openIndex === index ? 'auto' : 0,
                    opacity: openIndex === index ? 1 : 0
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 pt-2 sm:px-8">
                    <p
                      className="text-base leading-7 text-[#5a6552]"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(faq.answer) }}
                    />
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-14 text-center sm:mt-16"
        >
          <p className="mb-6 text-base text-[#5a6552] sm:text-lg">
            Staat jouw vraag er niet tussen?
          </p>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <a
              href="https://wa.me/31850233564"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-md bg-[#1e2b22] px-6 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(30,43,34,0.18)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#5a6552] hover:shadow-[0_14px_34px_rgba(30,43,34,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#fcfaf6] sm:px-7"
            >
              <img
                src="/assets/glyph/whatsapp.svg"
                alt="WhatsApp"
                className="w-5 h-5"
              />
              Persoonlijk contact via WhatsApp
            </a>
            <a
              href="tel:+31850233564"
              className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-md bg-[#e7e1d6] px-6 text-sm font-semibold text-[#1e2b22] ring-1 ring-inset ring-[#1e2b22]/20 transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#e7e1d6] hover:shadow-[0_14px_34px_rgba(30,43,34,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1e2b22] focus-visible:ring-offset-2 focus-visible:ring-offset-[#fcfaf6] sm:px-7"
            >
              <Headset className="w-5 h-5" />
              Telefonisch bereikbaar
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
